import { z } from 'zod'

import { TaskStatusTypeEnum } from './task-status'

// =====================
// Task→ticket status sync config
// =====================
//
// Stored in TicketTypeDef.taskSyncConfig (Json). Governs how a ticket follows
// the tasks it was promoted/split to (linked via ModuleReference TICKET→TASK):
//   - onAnyTaskActive: stage when any linked task moves to IN_PROGRESS
//   - onAllTasksCompleted: stage when every linked task is COMPLETED
//   - onTaskReopened: stage when a resolved ticket's task is reopened
//     (moved off COMPLETED without anything actively in progress)
// Key semantics: absent → category-matched default stage for that event;
// explicit null → that event never moves the ticket; a stageId that no longer
// exists in the type's effective stage set falls back to the category default.
// A ticket resolves only when EVERY linked, non-deleted, non-canceled task is
// complete. CLOSED tickets are terminal and never moved by sync.
export const TicketTaskSyncConfigSchema = z.object({
  // Default true — an empty config keeps sync on with category defaults.
  enabled: z.boolean().optional(),
  onAnyTaskActive: z.string().max(50).nullable().optional(),
  onAllTasksCompleted: z.string().max(50).nullable().optional(),
  onTaskReopened: z.string().max(50).nullable().optional(),
})

export type TicketTaskSyncConfig = z.infer<typeof TicketTaskSyncConfigSchema>

// =====================
// Linked task progress
// =====================

export interface LinkedTaskProgressSummary {
  /** Linked tasks that count toward the ticket (canceled ones excluded). */
  total: number
  /** Counted tasks whose status category is COMPLETED. */
  completed: number
  /** True only when there is at least one counted task and all are complete. */
  isAllComplete: boolean
}

/**
 * The ticket's linked-task progress, using the exact rule the sync engine
 * resolves `onAllTasksCompleted` with — so any indicator built on this can
 * never disagree with the automation.
 *
 * Canceled tasks drop out of BOTH numerator and denominator: they are
 * definitionally never going to complete, so counting them would hold the
 * ticket open forever. Deleted tasks are already filtered out upstream (the
 * task lookups behind linked-task summaries all scope to `deletedAt: null`).
 * A task with no resolved status category counts as incomplete.
 */
export function summarizeLinkedTaskProgress(
  tasks: { statusTypeId?: string | null }[],
): LinkedTaskProgressSummary {
  const counted = tasks.filter(
    task => task.statusTypeId !== TaskStatusTypeEnum.CANCELED,
  )
  const completed = counted.filter(
    task => task.statusTypeId === TaskStatusTypeEnum.COMPLETED,
  ).length
  return {
    total: counted.length,
    completed,
    isAllComplete: counted.length > 0 && completed === counted.length,
  }
}
